import React, { useState } from "react";
import styled, { css } from "styled-components";
import {
  FiArrowLeft,
  FiFileText,
  FiUser,
  FiMapPin,
  FiDollarSign,
  FiPackage,
  FiFile,
  FiAlertCircle,
  FiCopy,
  FiCheck,
  FiExternalLink,
} from "react-icons/fi";
import { HiOutlineDocumentText } from "react-icons/hi";
import api from "../../services/api";
import { tokens, surface, hairline } from "../../styles/designTokens";

const _mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const _eyebrow = css`
  ${_mono};
  font-size: 0.58em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

/* ═══════ STYLED COMPONENTS ═══════ */

const DetailContainer = styled.div`
  animation: detailFadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;

  @keyframes detailFadeIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0 8px 0;
  margin-bottom: 1.5em;

  align-items: flex-start;

  .back-btn {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    cursor: pointer;
    padding: 0;
    margin-top: 4px;
    flex-shrink: 0;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    svg { font-size: 1em; }

    &:hover {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
      transform: translateX(-1px);
    }
  }

  .header-details {
    flex: 1;
    min-width: 0;

    h3 {
      margin: 2px 0 8px 0;
      font-size: 1.4em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;

      &::before {
        content: "MOVIMENTAÇÃO · NF-e";
        display: block;
        ${_eyebrow};
        opacity: 0.4;
        margin-bottom: 4px;
      }
    }

    .header-meta {
      display: flex;
      gap: 6px;
      align-items: center;
      flex-wrap: wrap;

      span {
        ${_mono};
        display: inline-flex;
        align-items: center;
        padding: 3px 9px;
        border-radius: ${tokens.radiusPill};
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        border: 1px solid;
      }

      .status-tag {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};

        &.cancelled {
          background: ${tokens.dangerSoft};
          color: ${tokens.danger};
          border-color: rgba(239, 68, 68, 0.22);
        }
      }

      .type-tag {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
      }

      .date-tag {
        background: ${({ theme }) => surface(theme, 1)};
        color: ${({ theme }) => theme.text};
        border-color: ${({ theme }) => hairline(theme)};
        opacity: 0.7;
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;

    button {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      opacity: 0.85;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover {
        opacity: 1;
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};
      }

      &.btn-primary {
        background: ${tokens.brand};
        border-color: ${tokens.brand};
        color: #fff;
        opacity: 1;

        &:hover {
          background: ${tokens.brandHover};
          border-color: ${tokens.brandHover};
          transform: translateY(-1px);
        }
      }
    }
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;

    .header-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .header-actions button span { display: none; }
  }
`;

const HeroCard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 1.5em;

  .hero-item {
    padding: 16px 18px;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: border-color ${tokens.durBase} ${tokens.easeOut};

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
    }

    .hero-label {
      ${_eyebrow};
      font-size: 0.55em;
      opacity: 0.45;
    }

    .hero-value {
      ${_mono};
      font-size: 0.95em;
      color: ${({ theme }) => theme.text};
      font-weight: 500;
      line-height: 1.3;
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &.highlight {
        font-size: 1.3em;
        font-weight: 600;
        color: ${tokens.brand};
        letter-spacing: -0.02em;
      }

      &.mono {
        font-size: 0.78em;
        font-weight: 500;
        opacity: 0.5;
        letter-spacing: 0.02em;
      }
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const TabsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 1.5em;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  button {
    padding: 11px 16px;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    ${_mono};
    font-size: 0.66em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover { opacity: 0.8; }

    &.active {
      opacity: 1;
      color: ${tokens.brand};
      border-bottom-color: ${tokens.brand};
    }

    svg { font-size: 1.2em; }
  }

  /* Botão DANFE: CTA verde flat com glow sutil, separado das abas */
  button.pdf-btn {
    margin-left: auto;
    margin-bottom: 6px;
    padding: 9px 18px;
    background: ${tokens.brand};
    border: 1px solid ${tokens.brand};
    border-bottom: 1px solid ${tokens.brand};
    border-radius: ${tokens.radiusMd};
    color: #fff;
    opacity: 1;
    font-family: ${tokens.fontSans};
    font-size: 0.82em;
    font-weight: 600;
    text-transform: none;
    letter-spacing: -0.005em;
    gap: 8px;
    box-shadow: 0 2px 10px ${tokens.brandGlow};

    svg { font-size: 1em; }

    .ext-icon {
      font-size: 0.85em;
      opacity: 0.75;
      transition: transform ${tokens.durBase} ${tokens.easeOut};
    }

    &:hover:not(:disabled) {
      opacity: 1;
      background: ${tokens.brandHover};
      border-color: ${tokens.brandHover};
      transform: translateY(-1px);
      box-shadow: 0 6px 18px ${tokens.brandGlow};

      .ext-icon { transform: translate(2px, -2px); opacity: 1; }
    }

    &:active:not(:disabled) { transform: translateY(0); }

    &:disabled {
      opacity: 0.6;
      cursor: wait;
      box-shadow: none;
    }
  }
`;

const DetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SectionCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  margin-bottom: 20px;

  &:last-child { margin-bottom: 0; }
`;

const SectionTitle = styled.h4`
  ${_eyebrow};
  font-size: 0.62em;
  opacity: 0.45;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { font-size: 1.1em; }
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const DataItem = styled.div`
  background: ${(props) => props.$highlight
    ? tokens.brandSoft
    : surface(props.theme, 1)};
  border: 1px solid ${(props) => props.$highlight
    ? tokens.brandRing
    : hairline(props.theme)};
  border-radius: ${tokens.radiusMd};
  padding: 14px 16px;
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:hover {
    border-color: ${(props) => props.$highlight
      ? tokens.brand
      : hairline(props.theme, true)};
  }

  .label {
    ${_eyebrow};
    font-size: 0.55em;
    opacity: 0.45;
    margin-bottom: 6px;
  }

  .value {
    ${_mono};
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};
    font-weight: 500;
    line-height: 1.4;
    letter-spacing: -0.01em;
  }

  &.full-width {
    grid-column: 1 / -1;
  }

  &.span-2 {
    grid-column: span 2;
  }
`;

const StatusBadge = styled.span`
  ${_mono};
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: ${tokens.radiusPill};
  font-size: 0.62em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border: 1px solid;
  background: ${(props) =>
    props.$status === "Autorizada" ? tokens.brandSoft : tokens.dangerSoft};
  color: ${(props) =>
    props.$status === "Autorizada" ? tokens.brand : tokens.danger};
  border-color: ${(props) =>
    props.$status === "Autorizada" ? tokens.brandRing : "rgba(239, 68, 68, 0.22)"};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const AlertCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: ${tokens.warnSoft};
  border: 1px solid rgba(245, 158, 11, 0.22);
  border-radius: ${tokens.radiusMd};
  color: ${tokens.warn};

  svg {
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .alert-content {
    .alert-title {
      font-weight: 700;
      font-size: 13px;
      margin-bottom: 3px;
    }
    .alert-desc {
      font-size: 12px;
      opacity: 0.7;
      line-height: 1.5;
    }
  }
`;

const ReformaDivider = styled.div`
  ${_eyebrow};
  padding: 10px 14px;
  margin: 4px 0;
  font-size: 0.55em;
  opacity: 0.6;
  grid-column: 1 / -1;
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusSm};
`;

const ItemsTable = styled.div`
  border-radius: ${tokens.radiusMd};
  overflow: hidden;
  border: 1px solid ${({ theme }) => hairline(theme)};

  @media (max-width: 768px) {
    overflow-x: auto;
  }
`;

const TableInner = styled.div`
  min-width: 580px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 72px 96px 110px;
  padding: 10px 16px;
  background: ${({ theme }) => surface(theme, 1)};
  ${_eyebrow};
  font-size: 0.5em;
  opacity: 0.45;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 72px 96px 110px;
  padding: 11px 16px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  font-size: 0.82em;
  color: ${({ theme }) => theme.text};
  transition: background ${tokens.durBase} ${tokens.easeOut};

  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => surface(theme, 1)}; }

  .item-number {
    ${_mono};
    font-weight: 500;
    opacity: 0.4;
    font-size: 0.85em;
  }

  .item-desc {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-value {
    ${_mono};
    font-weight: 500;
  }
`;

const PdfContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 550px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => hairline(theme)};

  @media (max-width: 480px) { min-height: 400px; }
`;

const PdfToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => surface(theme, 1)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .toolbar-label {
    font-size: 12px;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    svg { font-size: 14px; }
  }

  button {
    padding: 6px 12px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: 8px;
    color: ${({ theme }) => theme.text};
    font-size: 12px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s ease;
    &:hover { background: ${({ theme }) => surface(theme, 1)}; }
    svg { font-size: 13px; }
  }
`;

const PdfViewer = styled.iframe`
  width: 100%;
  flex: 1;
  border: none;
  background: #525659;
  min-height: 500px;
  @media (max-width: 480px) { min-height: 360px; }
`;

const LoadingPdf = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: ${({ theme }) => theme.text};
  gap: 12px;

  .spinner {
    width: 28px;
    height: 28px;
    border: 2.5px solid ${({ theme }) => hairline(theme)};
    border-top-color: ${tokens.brand};
    border-radius: 50%;
    animation: detailSpin 0.8s linear infinite;
  }

  @keyframes detailSpin {
    to { transform: rotate(360deg); }
  }

  .loading-text {
    font-size: 13px;
    opacity: 0.4;
    font-weight: 500;
  }
`;

/* ═══════ COMPONENT ═══════ */

const NFeDetailView = ({ doc, onBack }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  if (!doc) return null;

  const nfe = doc;

  const formatCurrency = (value) => {
    if (!value) return "R$ 0,00";
    return `R$ ${parseFloat(value).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("pt-BR");
  };

  const openDanfe = async () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
      return;
    }
    setLoadingPdf(true);
    try {
      const response = await api.get(`/movimentacoes/danfe/${nfe.chave}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      setPdfUrl(url);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Erro ao carregar DANFE:", error);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleCopyKey = () => {
    if (nfe.chave) {
      navigator.clipboard.writeText(nfe.chave);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const hasReforma =
    nfe.reformaTributaria &&
    Object.values(nfe.reformaTributaria || {}).some(
      (v) => v !== null && v !== undefined
    );

  const totalNota = formatCurrency(
    nfe.total?.vNFTot || nfe.total?.ICMSTot?.vNF || nfe.valor
  );

  return (
    <DetailContainer>
      {/* Header — same pattern as transactions list header */}
      <HeaderBar>
        <button className="back-btn" onClick={onBack} title="Voltar">
          <FiArrowLeft />
        </button>
        <div className="header-details">
          <h3>Nota Fiscal Eletrônica</h3>
          <div className="header-meta">
            <span className={`status-tag ${nfe.status !== "Autorizada" ? "cancelled" : ""}`}>
              {nfe.status || "N/A"}
            </span>
            <span className="type-tag">NFe {nfe.num || nfe.numero || "—"}</span>
            <span className="date-tag">Série {nfe.serie || "—"}</span>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={handleCopyKey} title="Copiar chave de acesso">
            {copiedKey ? <FiCheck /> : <FiCopy />}
            <span>{copiedKey ? "Copiado" : "Copiar chave"}</span>
          </button>
          <button className="btn-primary" onClick={openDanfe}>
            <FiFile />
            <span>{loadingPdf ? "Abrindo..." : "DANFE"}</span>
          </button>
        </div>
      </HeaderBar>

      {/* Hero summary card */}
      <HeroCard>
        <div className="hero-item">
          <div className="hero-label">Valor Total</div>
          <div className="hero-value highlight">{totalNota}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Emitente</div>
          <div className="hero-value">{nfe.emiNome || nfe.emitente || "N/A"}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Data Emissão</div>
          <div className="hero-value">{formatDate(nfe.dhEvento || nfe.data)}</div>
        </div>
        <div className="hero-item">
          <div className="hero-label">Chave de Acesso</div>
          <div className="hero-value mono">{nfe.chave || "N/A"}</div>
        </div>
      </HeroCard>

      {/* Tabs — pill style matching transactions */}
      <TabsBar>
        <button className="active">
          <FiFileText /> Informações
        </button>
        <button className="pdf-btn" onClick={openDanfe} disabled={loadingPdf}>
          <FiFileText />
          {loadingPdf ? "Abrindo..." : "Abrir DANFE"}
          {!loadingPdf && <FiExternalLink className="ext-icon" />}
        </button>
      </TabsBar>

      {/* Body */}
      <DetailBody>
            {/* Informações Gerais */}
            <SectionCard>
              <SectionTitle><FiFileText /> Informações Gerais</SectionTitle>
              <DataGrid>
                <DataItem>
                  <div className="label">Número</div>
                  <div className="value">{nfe.num || nfe.numero || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Série</div>
                  <div className="value">{nfe.serie || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Data/Hora Emissão</div>
                  <div className="value">{formatDate(nfe.dhEvento || nfe.data)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Status</div>
                  <div className="value">
                    <StatusBadge $status={nfe.status}>{nfe.status || "N/A"}</StatusBadge>
                  </div>
                </DataItem>
                <DataItem>
                  <div className="label">Natureza da Operação</div>
                  <div className="value">{nfe.natOp || "N/A"}</div>
                </DataItem>
                <DataItem>
                  <div className="label">CFOP</div>
                  <div className="value">{nfe.cfop || "N/A"}</div>
                </DataItem>
                <DataItem className="full-width">
                  <div className="label">Chave de Acesso</div>
                  <div className="value" style={{ fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: 13, opacity: 0.6, wordBreak: "break-all", letterSpacing: "0.5px" }}>
                    {nfe.chave || "N/A"}
                  </div>
                </DataItem>
              </DataGrid>
            </SectionCard>

            {/* Emitente + Destinatário side by side on desktop */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 16 }}>
              <SectionCard style={{ marginBottom: 0 }}>
                <SectionTitle><FiUser /> Emitente</SectionTitle>
                <DataGrid>
                  <DataItem className="span-2">
                    <div className="label">Nome/Razão Social</div>
                    <div className="value">{nfe.emiNome || nfe.emitente || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">CNPJ/CPF</div>
                    <div className="value">{nfe.emiDoc || nfe.cnpj || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Cidade</div>
                    <div className="value">{nfe.emiCidade || "N/A"}</div>
                  </DataItem>
                </DataGrid>
              </SectionCard>

              <SectionCard style={{ marginBottom: 0 }}>
                <SectionTitle><FiMapPin /> Destinatário</SectionTitle>
                <DataGrid>
                  <DataItem className="span-2">
                    <div className="label">Nome/Razão Social</div>
                    <div className="value">{nfe.destNome || nfe.destinatario || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">CNPJ/CPF</div>
                    <div className="value">{nfe.destDoc || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Cidade</div>
                    <div className="value">{nfe.destCidade || "N/A"}</div>
                  </DataItem>
                </DataGrid>
              </SectionCard>
            </div>

            {/* Valores */}
            <SectionCard>
              <SectionTitle><FiDollarSign /> Valores</SectionTitle>
              <DataGrid>
                <DataItem>
                  <div className="label">Valor dos Produtos</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vProd || nfe.total?.vProd)}</div>
                </DataItem>
                <DataItem $highlight>
                  <div className="label">Valor Total da Nota</div>
                  <div className="value" style={{ fontWeight: 800, fontSize: 16, color: "#10b981" }}>{totalNota}</div>
                </DataItem>
                <DataItem>
                  <div className="label">ICMS</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vICMS || nfe.total?.vICMS)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">IPI</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vIPI || nfe.total?.vIPI)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">PIS</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vPIS || nfe.total?.vPIS)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">COFINS</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vCOFINS || nfe.total?.vCOFINS)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Frete</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vFrete || nfe.total?.vFrete)}</div>
                </DataItem>
                <DataItem>
                  <div className="label">Desconto</div>
                  <div className="value">{formatCurrency(nfe.total?.ICMSTot?.vDesc || nfe.total?.vDesc)}</div>
                </DataItem>
              </DataGrid>
            </SectionCard>

            {/* Reforma Tributária */}
            <SectionCard>
              <SectionTitle><HiOutlineDocumentText /> Reforma Tributária (NT 2025.002)</SectionTitle>
              {!hasReforma ? (
                <AlertCard>
                  <FiAlertCircle />
                  <div className="alert-content">
                    <div className="alert-title">Campos da Reforma Tributária não preenchidos</div>
                    <div className="alert-desc">
                      Este fornecedor ainda não está emitindo notas com os campos obrigatórios da Reforma Tributária (NT 2025.002-RTC v1.34).
                    </div>
                  </div>
                </AlertCard>
              ) : (
                <DataGrid>
                  <ReformaDivider style={{ color: "#8b5cf6" }}>Imposto Seletivo (IS)</ReformaDivider>
                  <DataItem>
                    <div className="label">CST IS</div>
                    <div className="value">{nfe.reformaTributaria.CSTIS || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Classif. Trib. IS</div>
                    <div className="value">{nfe.reformaTributaria.cClassTribIS || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Base de Cálculo IS</div>
                    <div className="value">{nfe.reformaTributaria.vBCIS ? formatCurrency(nfe.reformaTributaria.vBCIS) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Alíquota IS</div>
                    <div className="value">{nfe.reformaTributaria.pIS ? `${nfe.reformaTributaria.pIS}%` : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Alíq. Específica IS</div>
                    <div className="value">{nfe.reformaTributaria.pISEspec ? `${nfe.reformaTributaria.pISEspec}%` : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Valor IS</div>
                    <div className="value" style={{ fontWeight: 700, color: "#8b5cf6" }}>{nfe.reformaTributaria.vIS ? formatCurrency(nfe.reformaTributaria.vIS) : "N/A"}</div>
                  </DataItem>

                  <ReformaDivider style={{ color: "#10b981" }}>IBS / CBS</ReformaDivider>
                  <DataItem>
                    <div className="label">CST</div>
                    <div className="value">{nfe.reformaTributaria.CST || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Classif. Tributária</div>
                    <div className="value">{nfe.reformaTributaria.cClassTrib || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Ind. Doação</div>
                    <div className="value">{nfe.reformaTributaria.indDoacao || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Base de Cálculo</div>
                    <div className="value">{nfe.reformaTributaria.vBC ? formatCurrency(nfe.reformaTributaria.vBC) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Alíq. IBS UF</div>
                    <div className="value">{nfe.reformaTributaria.pIBSUF ? `${nfe.reformaTributaria.pIBSUF}%` : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Valor IBS UF</div>
                    <div className="value" style={{ fontWeight: 700, color: "#10b981" }}>{nfe.reformaTributaria.vIBSUF ? formatCurrency(nfe.reformaTributaria.vIBSUF) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Alíq. IBS Municipal</div>
                    <div className="value">{nfe.reformaTributaria.pIBSMun ? `${nfe.reformaTributaria.pIBSMun}%` : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Valor IBS Mun.</div>
                    <div className="value" style={{ fontWeight: 700, color: "#10b981" }}>{nfe.reformaTributaria.vIBSMun ? formatCurrency(nfe.reformaTributaria.vIBSMun) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Alíq. CBS</div>
                    <div className="value">{nfe.reformaTributaria.pCBS ? `${nfe.reformaTributaria.pCBS}%` : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Valor CBS</div>
                    <div className="value" style={{ fontWeight: 700, color: "#10b981" }}>{nfe.reformaTributaria.vCBS ? formatCurrency(nfe.reformaTributaria.vCBS) : "N/A"}</div>
                  </DataItem>

                  <ReformaDivider style={{ color: "#3b82f6" }}>Totalizadores</ReformaDivider>
                  <DataItem>
                    <div className="label">Total IS</div>
                    <div className="value" style={{ fontWeight: 800, fontSize: 16, color: "#8b5cf6" }}>{nfe.reformaTributaria.ISTot ? formatCurrency(nfe.reformaTributaria.ISTot) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Total IBS/CBS</div>
                    <div className="value" style={{ fontWeight: 800, fontSize: 16, color: "#10b981" }}>{nfe.reformaTributaria.IBSCBSTot ? formatCurrency(nfe.reformaTributaria.IBSCBSTot) : "N/A"}</div>
                  </DataItem>

                  <ReformaDivider style={{ color: "#f59e0b" }}>Outros Campos</ReformaDivider>
                  <DataItem>
                    <div className="label">Data Prev. Entrega</div>
                    <div className="value">{nfe.reformaTributaria.dPrevEntrega ? formatDate(nfe.reformaTributaria.dPrevEntrega) : "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Cód. Mun. FG IBS</div>
                    <div className="value">{nfe.reformaTributaria.cMunFGIBS || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Tipo NF Débito</div>
                    <div className="value">{nfe.reformaTributaria.tpNFDebito || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Tipo NF Crédito</div>
                    <div className="value">{nfe.reformaTributaria.tpNFCredito || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Créd. Pres. IBS ZFM</div>
                    <div className="value">{nfe.reformaTributaria.tpCredPresIBSZFM || "N/A"}</div>
                  </DataItem>
                  <DataItem>
                    <div className="label">Ind. Bem Móvel Usado</div>
                    <div className="value">{nfe.reformaTributaria.indBemMovelUsado || "N/A"}</div>
                  </DataItem>
                </DataGrid>
              )}
            </SectionCard>

            {/* Itens */}
            {nfe.det && nfe.det.length > 0 && (
              <SectionCard>
                <SectionTitle><FiPackage /> Itens ({nfe.det.length})</SectionTitle>
                <ItemsTable>
                  <TableInner>
                    <TableHeader>
                      <div>Item</div>
                      <div>Descrição</div>
                      <div>Qtd</div>
                      <div>Val. Unit.</div>
                      <div>Val. Total</div>
                    </TableHeader>
                    {nfe.det.slice(0, 10).map((item, index) => (
                      <TableRow key={index}>
                        <div className="item-number">{item.nItem || index + 1}</div>
                        <div className="item-desc">{item.prod?.xProd || "N/A"}</div>
                        <div>{item.prod?.qCom || "N/A"}</div>
                        <div>{formatCurrency(item.prod?.vUnCom)}</div>
                        <div className="item-value">{formatCurrency(item.prod?.vProd)}</div>
                      </TableRow>
                    ))}
                    {nfe.det.length > 10 && (
                      <TableRow style={{ fontStyle: "italic", opacity: 0.35 }}>
                        <div></div>
                        <div>... e mais {nfe.det.length - 10} itens</div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </TableRow>
                    )}
                  </TableInner>
                </ItemsTable>
              </SectionCard>
            )}
      </DetailBody>
    </DetailContainer>
  );
};

export default NFeDetailView;
